<template>
  <h2>Add user to {{ organisation.name }}</h2>
  <form @submit.prevent="addUser">
    <div class="form-group mb-3">
      <label for="firstName" class="form-label">First name</label>
      <input type="text" id="firstName" class="form-control" v-model="firstName" required>
    </div>
    <div class="form-group mb-3">
      <label for="lastName" class="form-label">Last name</label>
      <input type="text" id="lastName" class="form-control" v-model="lastName" required>
    </div>
    <div class="form-group mb-3">
      <label for="username" class="form-label">Username</label>
      <input type="text" id="username" class="form-control" v-model="username" required>
    </div>
    <div class="form-group mb-3">
      <label for="email" class="form-label">Email address</label>
      <input type="email" id="email" class="form-control" v-model="email" required>
      <p v-if="emailError" class="text-danger">{{ emailError }}</p>
    </div>
    <div class="form-group mb-3">
      <label for="mobile" class="form-label">Mobile number</label>
      <input type="text" id="mobile" class="form-control" v-model="mobileNumber" required>
      <p v-if="mobileNumberError" class="text-danger">{{ mobileNumberError }}</p>
    </div>
    <button type="submit" class="btn btn-primary me-3">Add user</button>
    <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>
  </form>
</template>

<script>
import {onMounted, ref, watch} from "vue";
import {getCSRFToken} from "@/store/auth";
import {API_BASE_URL} from "@/config";

function debounce(fn, delay) {
  let timeoutID;
  return function (...args) {
    clearTimeout(timeoutID);
    timeoutID = setTimeout(() => fn.apply(this, args), delay);
  };
}

async function callCheckUsernameAPI(newUsername) {
  try {
    const response = await fetch(
        `${API_BASE_URL}/username-is-available`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken()
          },
          body: JSON.stringify({
            username: newUsername
          }),
          credentials: 'include'
        })
    const data = await response.json()
    return {status: data.status}
  } catch (err) {
    console.error('Error checking username', err)
    return {ok: false, message: 'An error occurred. Please try again.'}
  }
}

async function callValidateEmailAPI(email) {
  try {
    const response = await fetch(`${API_BASE_URL}/validate-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': getCSRFToken()
      },
      body: JSON.stringify({email}),
      credentials: 'include'
    })
    return await response.json()
  } catch (err) {
    console.error('Error validating email', err)
    return {status: 'invalid', errors: ['An error occurred. Please try again.']}
  }
}

async function callValidateMobileNumberAPI(mobileNumber) {
  try {
    const response = await fetch(`${API_BASE_URL}/validate-mobile-number`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': getCSRFToken()
      },
      body: JSON.stringify({mobile_number: mobileNumber}),
      credentials: 'include'
    });
    const data = await response.json();
    return data;
  } catch (err) {
    console.error('Error validating mobile number', err);
    return {status: 'invalid', errors: ['An error occurred. Please try again.']};
  }
}

export default {
  props: {
    organisation: {
      type: Object,
      required: true
    }
  },
  emits: ['close'],

  setup() {
    const firstName = ref('')
    const lastName = ref('')
    const username = ref('')
    const email = ref('')
    const emailValid = ref(false)
    const emailError = ref('')
    const mobileNumber = ref('')
    const mobileNumberValid = ref(false)
    const mobileNumberError = ref('')
    const usernameError = ref('')
    const usernameValid = ref(false)

    const validateUsername = debounce(async (newUsername) => {
      if (newUsername) {
        let result = await callCheckUsernameAPI(newUsername)
        let counter = 1;
        while (result.status !== "free") {
          const newUsernameWithNumber = `${newUsername}${counter}`;
          result = await callCheckUsernameAPI(newUsernameWithNumber);
          counter++;
        }
        if (counter > 1) {
          username.value = `${newUsername}${counter - 1}`;
        }
        usernameError.value = '';
        usernameValid.value = true;
      }
    }, 1000)

    // Convert username to lowercase and remove special characters
    // Also ensure username starts with a letter
    watch(username, (newUsername) => {
      let formattedUsername = newUsername.toLowerCase().replace(/[^a-z0-9+\-_@]/g, '')
      if (formattedUsername && !/^[a-z]/.test(formattedUsername)) {
        formattedUsername = formattedUsername.replace(/^[^a-z]+/, '')
      }
      username.value = formattedUsername
      validateUsername(formattedUsername)
    })


    // Watch firstName and lastName to dynamically update username
    // Split the firstName on spaces or on dash (-) sign.
    // If the firstName is split into multiple parts,
    // then take the first 2 letters of each part.
    // If the firstName is only split into one part,
    // take the first 3 letters of the firstName.
    watch([firstName, lastName], ([newFirstName, newLastName]) => {
      const trimmedFirstName = String(newFirstName).trim();
      const trimmedLastName = String(newLastName).trim();

      const firstNameParts = trimmedFirstName.split(/[\s-]+/);
      const formattedFirstName = firstNameParts.length > 1
          ? firstNameParts.map(part => part.slice(0, 1)).join('')
          : trimmedFirstName.slice(0, 3);

      const lastNameParts = trimmedLastName.split(' ');
      const formattedLastName = lastNameParts.length > 1
          ? lastNameParts.map(part => part.slice(0, 2)).join('')
          : trimmedLastName.slice(0, 3);

      username.value = (formattedFirstName + formattedLastName).toLowerCase();
    });

    const validateEmail = debounce(async (newEmail) => {
      if (newEmail) {
        const result = await callValidateEmailAPI(newEmail)
        console.log(result)
        if (result.status === 'valid') {
          emailError.value = ''
          emailValid.value = true
        } else {
          emailError.value = result.errors.join(', ')
          emailValid.value = false
        }
      } else {
        emailError.value = ''
        emailValid.value = false
      }
    }, 500)

    watch(email, (newEmail) => {
      validateEmail(newEmail)
    })

    const validateMobileNumber = debounce(async (newMobileNumber) => {
      if (newMobileNumber) {
        const result = await callValidateMobileNumberAPI(newMobileNumber)
        if (result.status === 'valid') {
          mobileNumberError.value = ''
          mobileNumberValid.value = true
        } else {
          mobileNumberError.value = result.errors.join(', ')
          mobileNumberValid.value = false
        }
      } else {
        mobileNumberError.value = ''
        mobileNumberValid.value = false
      }
    }, 500);

    watch(mobileNumber, (newMobileNumber) => {
      validateMobileNumber(newMobileNumber);
    })

    onMounted(() => {
      document.getElementById('firstName').focus();
    })

    return {
      firstName,
      lastName,
      username,
      email,
      emailError,
      mobileNumber,
      mobileNumberValid,
      mobileNumberError,
      usernameError,
      usernameValid
    }
  },
  methods: {
    async addUser() {
      try {
        const response = await fetch(
            `${API_BASE_URL}/organisations/${this.organisation.slug}/users`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCSRFToken()
              },
              body: JSON.stringify({
                first_name: this.firstName.trim(),
                last_name: this.lastName.trim(),
                username: this.username,
                email: this.email.trim(),
                mobile_number: this.mobileNumber
              }),
              credentials: 'include'
            }
        );
        if (response.ok) {
          const data = await response.json();
          console.log('User added:', data);
          this.$emit('user-added', data);
          this.$emit('close');
        } else {
          const errorData = await response.json();
          console.error('Error adding user:', errorData);
        }
      } catch (error) {
        console.error('Error adding user:', error);
      }
    }
  }
}
</script>